import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router';
import { useStore } from '@/app/StoreContext';
import { Button } from '@/components/ui/Button';
import { SQIcon } from '@/components/ui/SQIcon';
import { Avatar } from '@/components/ui/Avatar';
import { colorForPlayer } from '@/utils/colorForPlayer';
import { runtime } from '@/platform/runtime';
import { applyMainButton, hideMainButton } from '@/platform/mainButton';
import { fx } from '@/platform/feedback';
import { api } from '@/api';
import { pluralize } from '@/utils/pluralize';
import type { MatchDto } from '@/api/dto';
import { competitionRanks, isTie } from '@/domain/standings';
import styles from './MatchFinalView.module.css';

type MatchStandingDto = MatchDto['standings'][number];

// Normal podium display order: 2nd, 1st, 3rd
const PODIUM_ORDER = [1, 0, 2] as const;
const BAR_HEIGHTS = [96, 132, 74] as const;
const REVEAL_DELAYS = [0.3, 1.3, 0.7] as const;

// Tie podium: two equal gold columns + bronze
const TIE_BAR_HEIGHTS = [110, 110, 74] as const;
const TIE_REVEAL_DELAYS = [0.4, 0.9, 0.3] as const;

const CONFETTI_COLORS = ['#ffb454', '#6ad1c6', '#c98aff', '#4f8cff', '#ff6b9d'];

function useReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Hash-based pseudo-random in [0, 1) — unlike sin(seed + n) with a shared
// seed per particle, each (i, n) pair lands on an uncorrelated value, so
// per-particle properties don't trace a smooth curve across particles.
// (An earlier sin-based generator made particles visibly fall in a ring.)
function confettiRandom(i: number, n: number): number {
  const x = Math.sin(i * 12.9898 + n * 78.233 + 1) * 43758.5453;
  return x - Math.floor(x);
}

// Stable particle config per mount (avoid re-randomizing on re-render)
const CONFETTI_PARTICLES = Array.from({ length: 36 }, (_, i) => {
  const r = (n: number) => confettiRandom(i, n);
  return {
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    left: r(1) * 120 - 10,
    // Independent random horizontal drift — makes particles scatter as they
    // fall instead of all dropping straight down.
    dx: (r(8) - 0.5) * 160,
    rot: (i % 2 === 0 ? 1 : -1) * (180 + r(2) * 360),
    dy: 90 + r(3) * 40,
    dur: 0.8 + r(4) * 0.6,
    delay: r(5) * 0.4,
    w: 6 + Math.round(r(6) * 4),
    h: 6 + Math.round(r(7) * 4),
  };
});

interface PodiumColProps {
  standing: MatchStandingDto;
  place: number; // 1, 2, 3
  barHeight: number;
  delay: number;
}

function PodiumCol({ standing, place, barHeight, delay }: PodiumColProps) {
  const isWinner = place === 1;
  const reduced = useReducedMotion();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (!isWinner || reduced) return;
    const t = setTimeout(() => setShowConfetti(true), delay * 1000 + 50);
    return () => clearTimeout(t);
  }, [isWinner, reduced, delay]);

  return (
    <motion.div
      className={styles.podiumCol}
      data-place={place}
      initial={reduced ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: reduced ? 0 : delay, ease: 'easeOut' }}
      style={{ position: 'relative' }}
    >
      {showConfetti && (
        <div className={styles.confettiLayer} aria-hidden>
          {CONFETTI_PARTICLES.map((p, i) => (
            <span
              key={i}
              className={styles.confettiParticle}
              style={{
                left: `${p.left}%`,
                '--confetti-rot': `${p.rot}deg`,
                '--confetti-dy': `${p.dy}px`,
                '--confetti-dx': `${p.dx}px`,
                background: p.color,
                animationDuration: `${p.dur}s`,
                animationDelay: `${p.delay}s`,
                width: p.w,
                height: p.h,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}

      <div className={styles.podiumAbove}>
        <div className={styles.podiumIconWrap}>
          <SQIcon name={isWinner ? 'trophy' : 'medal'} size={isWinner ? 22 : 18} />
        </div>
        <Avatar name={standing.nickname} color={colorForPlayer(standing.playerId)} size={isWinner ? 48 : 36} />
        <span className={styles.podiumNick}>{standing.nickname}</span>
        <span className={styles.podiumScore}>
          {pluralize(standing.totalScore, 'очко', 'очка', 'очков')}
        </span>
      </div>
      <div className={styles.podiumBar} style={{ height: barHeight }}>
        <span className={styles.podiumBarNum}>{place}</span>
      </div>
    </motion.div>
  );
}

export const MatchFinalView = observer(function MatchFinalView() {
  const { match, room, auth, round } = useStore();
  const nav = useNavigate();
  const { code } = useParams<{ code: string }>();
  const info = match.matchInfo;
  const [restarting, setRestarting] = useState(false);
  const [showSoloConfetti, setShowSoloConfetti] = useState(false);
  const hapticsRef = useRef(false);
  const soloConfettiRef = useRef(false);
  const soloConfettiTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isHost = !!code && room.room?.hostPlayerId === auth.playerId;
  const reduced = useReducedMotion();

  const handleLeave = async () => {
    if (code) {
      try { await room.leave(); } catch { /* ignore */ }
    }
    nav('/');
  };

  const handleRestart = async () => {
    if (!code || restarting) return;
    setRestarting(true);
    try {
      await api.match.restart(code);
      fx.tap();
    } catch {
      setRestarting(false);
    }
  };

  useEffect(() => {
    if (!runtime.isMiniApp || !isHost) return;
    applyMainButton({
      text: 'Сыграть ещё',
      visible: true,
      active: !restarting,
      onClick: handleRestart,
    });
    return () => hideMainButton();
  }, [isHost, restarting]);

  useEffect(() => {
    if (!info && code) void match.loadMatchInfo(code);
  }, [code]);

  // Haptics on winner reveal
  useEffect(() => {
    if (!info || hapticsRef.current || reduced) return;
    hapticsRef.current = true;
    const t = setTimeout(() => fx.win(), 1350);
    return () => clearTimeout(t);
  }, [info, reduced]);

  const isSolo = room.room?.settings.mode === 'Solo';
  const kickerText = room.room?.settings.mode === 'Match' ? 'Матч окончен' : 'Раунд окончен';

  // Solo confetti: trigger once after match info loads if player scored.
  // `info` (MobX matchInfo) can get reassigned a second time shortly after
  // the first load (REST response, then a SignalR push) — cleaning up the
  // timer on every re-run would cancel it right before it fires, with the
  // ref guard then blocking any retry. Only clear it on real unmount.
  useEffect(() => {
    if (!info || soloConfettiRef.current || !isSolo || reduced) return;
    if ((info.standings[0]?.totalScore ?? 0) <= 0) return;
    soloConfettiRef.current = true;
    soloConfettiTimeoutRef.current = setTimeout(() => setShowSoloConfetti(true), 600);
  }, [info, isSolo, reduced]);

  useEffect(() => () => {
    if (soloConfettiTimeoutRef.current) clearTimeout(soloConfettiTimeoutRef.current);
  }, []);

  // Solo word list: prefer in-memory round words, fall back to round results snapshot
  const myPlayerId = auth.playerId;
  const resultPlayerWords = match.roundResults?.players?.find(
    p => p.playerId === myPlayerId,
  )?.words;
  const soloWords: Array<{ word: string; score: number }> = round.myWords.length > 0
    ? [...round.myWords].sort((a, b) => b.rawScore - a.rawScore).map(w => ({ word: w.word, score: w.rawScore }))
    : (resultPlayerWords?.slice().sort((a, b) => b.finalScore - a.finalScore).map(w => ({ word: w.word, score: w.finalScore })) ?? []);

  if (!info) return <p className={styles.loading}>Загружаем итоги…</p>;

  const sorted = [...info.standings].sort((a, b) => b.totalScore - a.totalScore);
  const tieMode = !isSolo && isTie(sorted);
  const ranks = competitionRanks(sorted);

  // Build podium column entries
  type PodiumEntry = { standing: MatchStandingDto; place: number; barHeight: number; delay: number };
  let podiumEntries: PodiumEntry[] = [];
  if (tieMode) {
    const tiedPlayers = sorted.filter(s => ranks.get(s.playerId) === 1).slice(0, 2);
    const thirdPlayer = sorted.find(s => ranks.get(s.playerId) === 3);
    podiumEntries = [
      ...tiedPlayers.map((s, i) => ({
        standing: s,
        place: 1,
        barHeight: TIE_BAR_HEIGHTS[i] ?? 110,
        delay: TIE_REVEAL_DELAYS[i] ?? 0.5,
      })),
      ...(thirdPlayer ? [{ standing: thirdPlayer, place: 3, barHeight: 74, delay: 0.3 }] : []),
    ];
  } else {
    podiumEntries = PODIUM_ORDER
      .map((idx, colIdx) => ({
        standing: sorted[idx],
        place: idx + 1,
        barHeight: BAR_HEIGHTS[colIdx],
        delay: REVEAL_DELAYS[colIdx],
      }))
      .filter(e => e.standing != null) as PodiumEntry[];
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {isSolo ? (
          <h1 className={styles.heading}>Игра окончена</h1>
        ) : tieMode ? (
          <>
            <p className={styles.kicker}>{kickerText}</p>
            <h2 className={styles.heading}>Делят первое место</h2>
          </>
        ) : (
          <>
            <p className={styles.kicker}>{kickerText}</p>
            <h2 className={styles.heading}>Победитель</h2>
          </>
        )}
      </div>

      {isSolo ? (
        <div className={styles.soloResult} style={{ position: 'relative' }}>
          {showSoloConfetti && (
            <div className={styles.confettiLayer} aria-hidden>
              {CONFETTI_PARTICLES.map((p, i) => (
                <span
                  key={i}
                  className={styles.confettiParticle}
                  style={{
                    left: `${p.left}%`,
                    '--confetti-rot': `${p.rot}deg`,
                    '--confetti-dy': `${p.dy}px`,
                    background: p.color,
                    animationDuration: `${p.dur}s`,
                    animationDelay: `${p.delay}s`,
                    width: p.w,
                    height: p.h,
                  } as React.CSSProperties}
                />
              ))}
            </div>
          )}
          {info.standings[0]?.roundScores.map((sc, i) => (
            <div key={i} className={styles.soloRound}>
              <span>Раунд {i + 1}</span>
              <span>{pluralize(sc, 'очко', 'очка', 'очков')}</span>
            </div>
          ))}
          <div className={styles.soloTotal}>
            <span>Итого</span>
            <strong>{pluralize(info.standings[0]?.totalScore ?? 0, 'очко', 'очка', 'очков')}</strong>
          </div>
          {soloWords.length > 0 && (
            <div className={styles.soloWordList}>
              <p className={styles.soloWordListLabel}>Найдено слов: {soloWords.length}</p>
              <div className={styles.soloWordGrid}>
                {soloWords.map(({ word, score }) => (
                  <div key={word} className={styles.soloWordChip}>
                    <span>{word}</span>
                    <span className={styles.soloWordScore}>{score}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          {podiumEntries.length >= 2 && (
            <div className={styles.podiumWrap}>
              {podiumEntries.map(entry => (
                <PodiumCol
                  key={entry.standing.playerId}
                  standing={entry.standing}
                  place={entry.place}
                  barHeight={entry.barHeight}
                  delay={entry.delay}
                />
              ))}
            </div>
          )}

          {sorted.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {tieMode && <th>#</th>}
                    <th>Игрок</th>
                    {sorted[0].roundScores.map((_, i) => (
                      <th key={i}>Р{i + 1}</th>
                    ))}
                    <th>Итог</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(s => {
                    // TODO(D12-03): pass hasLeft when MatchStandingDto gains the field from backend
                    const hasLeft = (s as { hasLeft?: boolean }).hasLeft ?? false;
                    const rowClass = [
                      s.playerId === auth.playerId ? styles.rowSelf : '',
                      hasLeft ? styles.rowLeft : '',
                    ].filter(Boolean).join(' ') || undefined;
                    return (
                      <tr key={s.playerId} className={rowClass}>
                        {tieMode && <td>{ranks.get(s.playerId)}</td>}
                        <td>{s.nickname}</td>
                        {s.roundScores.map((sc, i) => <td key={i}>{sc}</td>)}
                        <td><strong>{s.totalScore}</strong></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className={styles.actions}>
        {isHost && !runtime.isMiniApp && (
          <Button onClick={handleRestart} disabled={restarting}>
            Сыграть ещё
          </Button>
        )}
        <Button variant="outline" onClick={handleLeave}>Выйти в главную</Button>
      </div>
    </div>
  );
});
